import { useState, useEffect, useRef } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Plus, Layout, Pencil, Trash2, Eye,
  GripVertical, Upload, X, Image as ImageIcon,
  Loader2, BarChart3, Copy, Check
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// ============ THEMES ============
const themes = [
  { value: "purple",  label: "Purple",   color: "#7c3aed" },
  { value: "blue",    label: "Blue",     color: "#3b82f6" },
  { value: "green",   label: "Green",    color: "#10b981" },
  { value: "pink",    label: "Pink",     color: "#ec4899" },
  { value: "orange",  label: "Orange",   color: "#f59e0b" },
  { value: "red",     label: "Red",      color: "#ef4444" },
  { value: "teal",    label: "Teal",     color: "#14b8a6" },
  { value: "indigo",  label: "Indigo",   color: "#6366f1" },
];

// ============ TEMPLATES ============
const templates = [
  { value: "classic",  label: "Classic",  desc: "Avatar atas, link bawah" },
  { value: "card",     label: "Card",     desc: "Cover banner + avatar" },
  { value: "minimal",  label: "Minimal",  desc: "Clean, teks saja" },
  { value: "bold",     label: "Bold",     desc: "Background penuh, besar" },
];

// ============ IMAGE UPLOAD COMPONENT ============
const ImageUpload = ({ label, value, onChange, endpoint, aspectRatio = "square", hint }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const inputRef = useRef();

  useEffect(() => { setPreview(value || ""); }, [value]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview lokal dulu
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}${endpoint}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      onChange(res.data.url);
      toast.success("Gambar berhasil diupload!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload gambar");
      setPreview(value || "");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const isSquare = aspectRatio === "square";

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div
        className={`relative border-2 border-dashed border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary transition-colors ${isSquare ? "w-24 h-24" : "w-full h-28"}`}
        onClick={() => !isUploading && inputRef.current?.click()}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              className={`w-full h-full object-cover ${isSquare ? "rounded-full" : ""}`}
            />
            <button
              type="button"
              className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 z-10"
              onClick={(e) => { e.stopPropagation(); handleRemove(); }}
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-5 h-5" />
                <span className="text-xs text-center px-1">Upload</span>
              </>
            )}
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
};

// ============ LIVE PREVIEW ============
const MicrositePreview = ({ title, bio, avatar, cover, background, theme, template, links }) => {
  const themeColor = themes.find(t => t.value === theme)?.color || "#7c3aed";
  const initial = title ? title.charAt(0).toUpperCase() : "M";
  const visibleLinks = links.filter(l => l.title);

  const bgStyle = background
    ? { backgroundImage: `url(${background})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: template === "bold"
        ? `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}99 100%)`
        : `linear-gradient(180deg, ${themeColor}22 0%, transparent 50%)` };

  return (
    <div className="w-[200px] h-[380px] rounded-2xl overflow-hidden border border-border shadow-lg mx-auto bg-card">
      <div className="w-full h-full overflow-y-auto" style={bgStyle}>

        {/* CARD template — cover banner */}
        {template === "card" && (
          <div className="relative h-16 w-full" style={{
            background: cover ? `url(${cover}) center/cover` : `linear-gradient(135deg, ${themeColor}, ${themeColor}88)`
          }}>
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2">
              {avatar ? (
                <img src={avatar} alt="" className="w-12 h-12 rounded-full border-2 border-white object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: themeColor }}>
                  {initial}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`p-4 text-center ${template === "card" ? "pt-9" : "pt-6"}`}>
          {/* CLASSIC / BOLD / MINIMAL — avatar atas */}
          {template !== "card" && (
            <div className="mb-3">
              {avatar ? (
                <img src={avatar} alt="" className="w-16 h-16 rounded-full mx-auto object-cover border-2 border-white shadow" />
              ) : (
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xl shadow"
                  style={{ backgroundColor: template === "bold" ? "rgba(255,255,255,0.2)" : themeColor }}>
                  {initial}
                </div>
              )}
            </div>
          )}

          <p className={`font-bold text-sm leading-tight ${template === "bold" ? "text-white" : ""}`}>
            {title || "My Bio Links"}
          </p>
          {bio && (
            <p className={`text-xs mt-1 line-clamp-2 ${template === "bold" ? "text-white/80" : "text-muted-foreground"}`}>
              {bio}
            </p>
          )}

          {/* MINIMAL — garis pemisah */}
          {template === "minimal" && <div className="my-3 border-t border-border" />}

          <div className="space-y-2 mt-3">
            {visibleLinks.length === 0 && (
              <div className="text-xs text-muted-foreground opacity-50">Link akan muncul di sini</div>
            )}
            {visibleLinks.map((link, i) => (
              <div key={i} className={`py-2 px-3 rounded-lg text-xs font-medium transition-all
                ${template === "minimal"
                  ? "flex justify-between items-center border-b border-border/50 rounded-none"
                  : template === "bold"
                  ? "bg-white/20 text-white"
                  : "border"
                }`}
                style={template !== "minimal" && template !== "bold"
                  ? { borderColor: `${themeColor}44`, color: themeColor }
                  : {}}>
                {link.title}
                {template === "minimal" && <span className="text-muted-foreground">→</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
const MicrositesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [microsites, setMicrosites] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMicrosite, setSelectedMicrosite] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("basic"); // basic | links | design

  // Form states
  const [formTitle, setFormTitle]           = useState("");
  const [formBio, setFormBio]               = useState("");
  const [formAvatar, setFormAvatar]         = useState("");
  const [formCover, setFormCover]           = useState("");
  const [formBackground, setFormBackground] = useState("");
  const [formTheme, setFormTheme]           = useState("purple");
  const [formTemplate, setFormTemplate]     = useState("classic");
  const [formLinks, setFormLinks]           = useState([{ title: "", url: "" }]);
  const [formCustomSlug, setFormCustomSlug] = useState("");
  const [slugStatus, setSlugStatus]         = useState(null);

  useEffect(() => { fetchMicrosites(); }, []);

  // Realtime slug checker dengan debounce
  useEffect(() => {
    const delay = setTimeout(() => {
      if (formCustomSlug) {
        checkSlugAvailability(formCustomSlug);
      } else {
        setSlugStatus(null);
      }
    }, 500);
    return () => clearTimeout(delay);
  }, [formCustomSlug]);

  const fetchMicrosites = async () => {
    try {
      const res = await axios.get(`${API}/microsites`, { withCredentials: true });
      setMicrosites(res.data);
    } catch {
      toast.error("Gagal memuat microsites");
    } finally {
      setIsLoading(false);
    }
  };

  
  const checkSlugAvailability = async (slug) => {
    if (slug.length < 3) { setSlugStatus("too_short"); return; }
    const valid = /^[a-z0-9\-]+$/.test(slug);
    if (!valid) { setSlugStatus("invalid"); return; }

    setSlugStatus("checking");
    try {
      const excludeParam = editMode && selectedMicrosite
        ? `?exclude_id=${selectedMicrosite.microsite_id}`
        : "";
      const res = await axios.get(`${API}/microsites/check-slug/${slug}${excludeParam}`);
      setSlugStatus(res.data.available ? "available" : "taken");
    } catch {
      setSlugStatus("taken");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formTitle) { toast.error("Title harus diisi"); return; }

    setIsSubmitting(true);
    const data = {
      title: formTitle,
      bio: formBio || null,
      avatar_url: formAvatar || null,
      cover_url: formCover || null,
      background_url: formBackground || null,
      theme: formTheme,
      template: formTemplate,
      links: formLinks.filter(l => l.title && l.url),
      custom_slug: formCustomSlug || null,
    };

    try {
      if (editMode && selectedMicrosite) {
        const res = await axios.patch(`${API}/microsites/${selectedMicrosite.microsite_id}`, data, { withCredentials: true });
        setMicrosites(microsites.map(m => m.microsite_id === selectedMicrosite.microsite_id ? res.data : m));
        toast.success("Microsite berhasil diupdate!");
      } else {
        const res = await axios.post(`${API}/microsites`, data, { withCredentials: true });
        setMicrosites([res.data, ...microsites]);
        toast.success("Microsite berhasil dibuat!");
      }
      resetForm();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan microsite");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Yakin ingin menghapus microsite ini?")) return;
    try {
      await axios.delete(`${API}/microsites/${id}`, { withCredentials: true });
      setMicrosites(microsites.filter(m => m.microsite_id !== id));
      toast.success("Microsite berhasil dihapus!");
    } catch { toast.error("Gagal menghapus microsite"); }
  };

  const openEditDialog = (ms) => {
    setSelectedMicrosite(ms);
    setFormTitle(ms.title);
    setFormBio(ms.bio || "");
    setFormAvatar(ms.avatar_url || "");
    setFormCover(ms.cover_url || "");
    setFormBackground(ms.background_url || "");
    setFormTheme(ms.theme);
    setFormTemplate(ms.template || "classic");
    setFormLinks(ms.links.length > 0 ? ms.links : [{ title: "", url: "" }]);
    setFormCustomSlug(ms.slug || "");
    setSlugStatus(null);
    setEditMode(true);
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormTitle(""); setFormBio(""); setFormAvatar("");
    setFormCover(""); setFormBackground("");
    setFormTheme("purple"); setFormTemplate("classic");
    setFormLinks([{ title: "", url: "" }]);
    setFormCustomSlug("");
    setSlugStatus(null);
    setSelectedMicrosite(null);
  };

  const addLink = () => setFormLinks([...formLinks, { title: "", url: "" }]);
  const updateLink = (i, field, val) => {
    const n = [...formLinks]; n[i][field] = val; setFormLinks(n);
  };
  const removeLink = (i) => setFormLinks(formLinks.filter((_, idx) => idx !== i));

  const getThemeColor = (t) => themes.find(x => x.value === t)?.color || "#7c3aed";
  
  const copyMicrositeUrl = async (ms) => {
    const url = `${window.location.origin}/m/${ms.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(ms.microsite_id);
      toast.success("URL microsite disalin!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Gagal menyalin URL");
    }
  };

  // ======= RENDER =======
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="microsites-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Microsites
            </h1>
            <p className="text-muted-foreground mt-1">Buat halaman bio link yang menarik</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-microsite-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Microsite
          </Button>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : microsites.length === 0 ? (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <Layout className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">Belum ada microsite</h3>
              <p className="text-sm text-muted-foreground mb-4">Buat microsite pertama Anda</p>
              <Button onClick={openCreateDialog}><Plus className="w-4 h-4 mr-2" />Create Microsite</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {microsites.map((ms) => (
              <Card key={ms.microsite_id} className="dashboard-card" data-testid={`microsite-${ms.microsite_id}`}>
                {/* Cover preview */}
                <div className="h-20 rounded-t-xl overflow-hidden relative"
                  style={{
                    background: ms.cover_url
                      ? `url(${ms.cover_url}) center/cover`
                      : `linear-gradient(135deg, ${getThemeColor(ms.theme)}44, ${getThemeColor(ms.theme)}22)`
                  }}>
                  <div className="absolute bottom-0 left-4 translate-y-1/2">
                    {ms.avatar_url ? (
                      <img src={ms.avatar_url} alt={ms.title}
                        className="w-12 h-12 rounded-full border-2 border-card object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full border-2 border-card flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: getThemeColor(ms.theme) }}>
                        {ms.title.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="text-xs bg-black/40 text-white px-2 py-0.5 rounded-full capitalize">
                      {ms.template || "classic"}
                    </span>
                  </div>
                </div>

                <CardContent className="pt-8 pb-4">
                  <h3 className="font-semibold">{ms.title}</h3>
                  {ms.bio && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ms.bio}</p>}
                  <p className="text-xs text-primary font-mono mt-1 truncate">
                    {window.location.origin}/m/{ms.slug} 
                  </p>
                  
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground">{ms.views} views · {ms.links?.length || 0} links</p>
                    <button
                      onClick={() => copyMicrositeUrl(ms)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                      title="Copy URL"
                    >
                      {copiedId === ms.microsite_id ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      <span className="font-mono truncate max-w-[140px]">
                        /m/{ms.slug}
                      </span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1"
                      onClick={() => window.open(`/m/${ms.slug}`, "_blank")}
                      data-testid={`view-${ms.microsite_id}`}>
                      <Eye className="w-4 h-4 mr-2" /> Preview
                    </Button>
                    
                    <Button variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => navigate(`/microsites/${ms.microsite_id}/analytics`)}
                      data-testid={`analytics-${ms.microsite_id}`}
                      title="Analytics">
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => openEditDialog(ms)} data-testid={`edit-${ms.microsite_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive"
                      onClick={() => handleDelete(ms.microsite_id)} data-testid={`delete-${ms.microsite_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Edit Microsite" : "Create Microsite"}</DialogTitle>
              <DialogDescription>
                {editMode ? "Update detail microsite Anda" : "Buat halaman bio link baru"}
              </DialogDescription>
            </DialogHeader>

            {/* Tab Navigator */}
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {[
                { id: "basic",  label: "Basic Info" },
                { id: "links",  label: "Links" },
                { id: "design", label: "Design & Gambar" },
              ].map(tab => (
                <button key={tab.id} type="button"
                  className={`flex-1 py-1.5 text-sm rounded-md transition-colors ${activeTab === tab.id ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-[1fr_220px] gap-6">
                {/* LEFT: Form */}
                <div className="space-y-4 min-h-[300px]">

                  {/* TAB: Basic Info */}
                  {activeTab === "basic" && (
                    <>
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input placeholder="My Bio Links" value={formTitle}
                          onChange={e => setFormTitle(e.target.value)} data-testid="microsite-title-input" />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Bio</Label>
                        <Textarea placeholder="Deskripsi singkat tentang Anda..." value={formBio}
                          onChange={e => setFormBio(e.target.value)} rows={3} data-testid="microsite-bio-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Custom URL
                          <span className="text-xs text-muted-foreground ml-1">(opsional)</span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {window.location.origin}/m/
                          </span>
                          <div className="relative flex-1">
                            <Input
                              placeholder="nama-kamu"
                              value={formCustomSlug}
                              onChange={(e) => {
                                const val = e.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z0-9\-]/g, "");
                                setFormCustomSlug(val);
                              }}
                            />
                            {slugStatus === "checking" && (
                              <span className="absolute right-2 top-2 text-xs text-muted-foreground">
                                memeriksa...
                              </span>
                            )}
                            {(slugStatus === "taken" || slugStatus === "invalid" || slugStatus === "too_short") && (
                              <span className="absolute right-2 top-2 text-red-500">✕</span>
                            )}
                          </div>
                        </div>

                        {slugStatus === "available" && (
                          <p className="text-xs text-green-500">✓ URL tersedia</p>
                        )}
                        {slugStatus === "taken" && (
                          <p className="text-xs text-red-500">URL sudah digunakan</p>
                        )}
                        {slugStatus === "invalid" && (
                          <p className="text-xs text-red-500">Hanya huruf kecil, angka, dan tanda (-)</p>
                        )}
                        {slugStatus === "too_short" && (
                          <p className="text-xs text-red-500">Minimal 3 karakter</p>
                        )}
                        {!formCustomSlug && (
                          <p className="text-xs text-muted-foreground">
                            Kosongkan untuk generate otomatis
                          </p>
                        )}
                      </div>
                      <ImageUpload
                        label="Foto Profil / Avatar"
                        value={formAvatar}
                        onChange={setFormAvatar}
                        endpoint="/upload/avatar"
                        aspectRatio="square"
                        hint="JPG/PNG/WebP, maks 5MB. Akan dioptimasi ke 400×400 WebP"
                      />
                    </>
                  )}

                  {/* TAB: Links */}
                  {activeTab === "links" && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Daftar Link</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={addLink}
                          data-testid="add-link-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add Link
                        </Button>
                      </div>
                      {formLinks.map((link, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <Input placeholder="Title" value={link.title}
                            onChange={e => updateLink(i, "title", e.target.value)}
                            className="flex-1" data-testid={`link-title-${i}`} />
                          <Input placeholder="URL" value={link.url}
                            onChange={e => updateLink(i, "url", e.target.value)}
                            className="flex-1" data-testid={`link-url-${i}`} />
                          {formLinks.length > 1 && (
                            <Button type="button" variant="ghost" size="icon"
                              className="h-9 w-9 text-destructive flex-shrink-0" onClick={() => removeLink(i)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* TAB: Design */}
                  {activeTab === "design" && (
                    <div className="space-y-5">
                      {/* Template */}
                      <div className="space-y-2">
                        <Label>Template</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {templates.map(t => (
                            <button key={t.value} type="button"
                              className={`p-3 rounded-xl border text-left transition-all ${formTemplate === t.value ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"}`}
                              onClick={() => setFormTemplate(t.value)}>
                              <p className="text-sm font-medium">{t.label}</p>
                              <p className="text-xs text-muted-foreground">{t.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tema Warna */}
                      <div className="space-y-2">
                        <Label>Tema Warna</Label>
                        <div className="flex flex-wrap gap-2">
                          {themes.map(t => (
                            <button key={t.value} type="button"
                              className={`w-8 h-8 rounded-full border-2 transition-all ${formTheme === t.value ? "border-foreground scale-110" : "border-transparent"}`}
                              style={{ backgroundColor: t.color }}
                              title={t.label}
                              onClick={() => setFormTheme(t.value)} />
                          ))}
                        </div>
                      </div>

                      {/* Cover */}
                      <ImageUpload
                        label="Cover / Banner"
                        value={formCover}
                        onChange={setFormCover}
                        endpoint="/upload/cover"
                        aspectRatio="wide"
                        hint="Dioptimasi ke 1200×400 WebP (template Card)"
                      />

                      {/* Background */}
                      <ImageUpload
                        label="Background Custom"
                        value={formBackground}
                        onChange={setFormBackground}
                        endpoint="/upload/background"
                        aspectRatio="wide"
                        hint="Dioptimasi ke 1920×1080 WebP"
                      />
                    </div>
                  )}
                </div>

                {/* RIGHT: Live Preview */}
                <div className="hidden md:block">
                  <Label className="mb-3 block text-center">Live Preview</Label>
                  <MicrositePreview
                    title={formTitle}
                    bio={formBio}
                    avatar={formAvatar}
                    cover={formCover}
                    background={formBackground}
                    theme={formTheme}
                    template={formTemplate}
                    links={formLinks}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    slugStatus === "checking" ||
                    slugStatus === "taken" ||
                    slugStatus === "invalid"
                  }
                  data-testid="microsite-submit-btn"
                >
                  {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editMode ? "Save Changes" : "Create Microsite"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MicrositesPage;